import { contentList, tx, txt, useContent } from "@/content/ContentContext";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Barcode, Boxes, Building2, Gauge, Globe2, Pill, Printer, ShieldCheck, Users } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BrandMark } from "@/components/AppShell";
import { HeroMockup } from "@/components/landing/HeroMockup";
import { PlanCards } from "@/components/PlanCards";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CURRENCIES, planCurrency } from "@/lib/plans";
import { ResellerApplyDialog, openResellerApply } from "@/components/ResellerApply";
import { ResellerSection } from "@/components/landing/ResellerSection";
import { useContact } from "@/context/ContactContext";
import { CONTACT_EMAIL } from "@/lib/plans";
import { AddonCard } from "@/components/AddonCard";
import { OnlineStoreSection } from "@/components/landing/OnlineStoreSection";
import { ReportsSection } from "@/components/landing/ReportsSection";
import { ProductsSection } from "@/components/landing/ProductsSection";
import { BACKEND } from "@/lib/host";

const IMG_PHARMA = "https://static.prod-images.emergentagent.com/jobs/83f0af28-42da-44aa-8822-7a7c19922e0a/images/512dad7103fbed9d88191090a8c56a90f0dc987beb4827e5ae80306cffe61069.jpeg";
const IMG_SCAN = "https://static.prod-images.emergentagent.com/jobs/83f0af28-42da-44aa-8822-7a7c19922e0a/images/d8d49c24b9a45c5b399ad4886c775bb6b002b30acf2c1b0f6d872427b57dc717.jpeg";
const PRICING_CUR_KEY = "samrtin_pricing_currency";

function DemoCTA({ id, tone = "amber", className = "" }) {
  const nav = useNavigate();
  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 ${className}`}>
      <button
        onClick={() => nav("/signup")}
        data-testid={`cta-demo-${id}`}
        className={`group inline-flex h-13 items-center gap-2 rounded-xl px-6 py-3.5 font-display text-base font-bold transition-[background-color,transform] active:scale-[0.98] ${
          tone === "amber" ? "bg-amber-500 text-slate-950 hover:bg-amber-400" : "bg-slate-950 text-white hover:bg-slate-800"
        }`}
      >
        {txt("landing.start_15_day_free_demo")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </button>
      <span className={`text-sm font-medium ${tone === "amber" ? "text-slate-400" : "text-slate-800"}`} data-testid={`cta-no-card-${id}`}>
        {txt("landing.no_credit_card_required")}
      </span>
    </div>
  );
}

export function Header() {
  useContent();
  const { session } = useAuth();
  const nav = useNavigate();
  return (
    <header className="grain sticky top-0 z-40 border-b border-white/5 bg-slate-950/95 backdrop-blur-md" data-testid="landing-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-6 px-4 sm:px-8">
        <Link to="/" data-testid="landing-logo"><BrandMark /></Link>
        <nav className="hidden items-center gap-6 text-sm font-semibold text-slate-400 md:flex">
          {[["products", tx("landing.products")], ["restaurant", tx("landing.restaurant")], ["features", tx("landing.features")], ["online-store", tx("landing.online_store")], ["reports", tx("landing.reports")], ["pricing", tx("landing.pricing")], ["faq", tx("landing.nav_faq")]].map(([id, l]) => (
            <a key={id} href={`/#${id}`} className="transition-colors hover:text-white" data-testid={`landing-nav-${id}`}>{l}</a>
          ))}
          <Link to="/how-it-works" className="transition-colors hover:text-white" data-testid="landing-nav-how">{txt("landing.how_it_works")}</Link>
          <button onClick={openResellerApply} className="transition-colors hover:text-white" data-testid="landing-nav-reseller">{txt("landing.become_a_reseller")}</button>
        </nav>
        <ResellerApplyDialog />
        <div className="ml-auto flex items-center gap-3">
          {session ? (
            <button onClick={() => nav("/pos")} data-testid="landing-open-pos" className="h-10 rounded-lg bg-amber-500 px-4 text-sm font-bold text-slate-950 hover:bg-amber-400">{txt("landing.open_pos")}</button>
          ) : (
            <>
              <Link to="/login" data-testid="landing-login" className="flex h-10 items-center rounded-lg px-3 text-sm font-semibold text-white hover:bg-white/10">{txt("landing.log_in")}</Link>
              <div className="hidden items-center gap-3 lg:flex">
                <span className="text-xs text-slate-500">{txt("landing.no_credit_card_required")}</span>
                <button onClick={() => nav("/signup")} data-testid="cta-demo-header" className="h-10 rounded-lg bg-amber-500 px-4 text-sm font-bold text-slate-950 hover:bg-amber-400">{txt("landing.start_15_day_free_demo")}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="grain relative overflow-hidden bg-slate-950 pb-28 pt-14 sm:pt-20" data-testid="landing-hero">
      <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-amber-500/10 blur-[120px]" />
      <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-sky-400/10 blur-[120px]" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-8 lg:grid-cols-[1.05fr_1fr]">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {txt("landing.cloud_pos_retail_pharmacy_restaurant")}
          </div>
          <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {txt("landing.the_counter_that_keeps_up")} <span className="text-amber-400">{txt("landing.queue")}</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
            {txt("landing.smart_in_retail_is_a")}
          </p>
          <a href="/#online-store" data-testid="hero-online-store" className="mt-5 inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-sm font-semibold text-amber-300 hover:bg-amber-400/20">
            <span className="rounded bg-amber-400 px-1.5 font-mono text-[10px] font-bold uppercase text-slate-950">{txt("landing.premium")}</span> {txt("landing.new_your_own_online_store")}
          </a>
          <DemoCTA id="hero" className="mt-9" />
          <div className="mt-10 grid max-w-lg grid-cols-3 gap-4 border-t border-white/10 pt-6">
            {[[tx("landing.stat_speed_value"), tx("landing.stat_speed")], [tx("landing.58_80mm"), tx("landing.stat_receipts")], [tx("landing.stat_countries_value"), tx("landing.stat_countries")]].map(([a, b]) => (
              <div key={b}>
                <div className="money text-lg font-bold text-white sm:text-xl">{a}</div>
                <div className="text-xs text-slate-500">{b}</div>
              </div>
            ))}
          </div>
        </div>
        <HeroMockup />
      </div>
    </section>
  );
}

const TAX = () => ([[tx("landing.pakistan"), "PKR", tx("landing.sales_tax_18")], ["UAE", "AED", "VAT 5%"], [tx("landing.oman"), "OMR", "VAT 5%"], [tx("landing.india"), "INR", "GST 18%"]]);

function TaxStrip() {
  return (
    <div className="relative z-10 mx-auto -mt-12 max-w-6xl px-4 sm:px-8">
      <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_50px_-30px_rgba(15,23,42,0.5)] md:grid-cols-4" data-testid="tax-strip">
        {TAX().map(([c, cur, t], i) => (
          <div key={c} className={`p-5 ${i % 2 ? "border-l" : ""} ${i > 1 ? "border-t md:border-t-0" : ""} md:border-l md:first:border-l-0 border-slate-100`}>
            <div className="eyebrow">{c}</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="money text-xl font-bold text-slate-900">{cur}</span>
              <span className="text-sm text-slate-500">{t}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const FEATURES = () => ([
  { icon: Gauge, title: tx("landing.fast_pos"), body: tx("landing.big_tap_targets_instant_totals") },
  { icon: Printer, title: tx("landing.thermal_receipt_printing"), body: tx("landing.receipts_sized_for_58mm_or") },
  { icon: Globe2, title: tx("landing.vat_gst_by_country"), body: tx("landing.sales_tax_vat_or_gst") },
  { icon: Building2, title: tx("landing.multi_branch"), body: tx("landing.each_branch_has_its_own"), tag: tx("landing.growth") },
  { icon: Users, title: tx("landing.staff_roles"), body: tx("landing.owner_branch_manager_and_cashier"), tag: tx("landing.growth") },
]);

function Features() {
  return (
    <section id="features" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-24 sm:px-8" data-testid="landing-features">
      <div className="max-w-2xl">
        <div className="eyebrow !text-amber-600">{txt("landing.features")}</div>
        <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{txt("landing.everything_a_busy_counter_needs")}</h2>
      </div>
      <div className="mt-12 grid gap-4 lg:grid-cols-6">
        <article className="group relative overflow-hidden rounded-3xl bg-slate-950 lg:col-span-4 lg:row-span-2" data-testid="feature-fefo">
          <img src={IMG_PHARMA} alt={tx("landing.pharmacy_counter_with_tablet_pos")} className="h-64 w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-[1.03] sm:h-80" loading="lazy" />
          <div className="p-7 sm:p-9">
            <div className="flex items-center gap-2 text-amber-400"><Pill className="h-5 w-5" /><span className="eyebrow !text-amber-400">{txt("landing.pharmacy")}</span></div>
            <h3 className="mt-3 font-display text-2xl font-bold text-white">{txt("landing.automatic_fefo_for_pharmacies")}</h3>
            <p className="mt-2 max-w-lg text-sm leading-relaxed text-slate-400">
              {txt("landing.every_add_picks_the_batch")}
            </p>
          </div>
        </article>
        <article className="group overflow-hidden rounded-3xl border border-slate-200 bg-white lg:col-span-2 lg:row-span-2" data-testid="feature-barcode">
          <img src={IMG_SCAN} alt={tx("landing.scanning_a_barcode_at_a")} className="h-48 w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" loading="lazy" />
          <div className="p-7">
            <Barcode className="h-6 w-6 text-slate-900" />
            <h3 className="mt-3 font-display text-xl font-bold text-slate-900">{txt("landing.barcode_scanning")}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{txt("landing.plug_in_any_usb_or")}</p>
          </div>
        </article>
        {FEATURES().map(({ icon: Icon, title, body, tag }, i) => (
          <article key={title} className={`rounded-3xl border border-slate-200 bg-white p-7 transition-[border-color,transform] hover:-translate-y-0.5 hover:border-slate-900 ${i < 2 ? "lg:col-span-3" : "lg:col-span-2"}`} data-testid={`feature-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="flex items-center justify-between">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-slate-950 text-amber-400"><Icon className="h-5 w-5" /></span>
              {tag && <span className="rounded-full bg-amber-100 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-amber-800">{tag}</span>}
            </div>
            <h3 className="mt-5 font-display text-xl font-bold text-slate-900">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

const STEPS = () => ([
  ["01", tx("landing.sign_up_in_a_minute"), tx("landing.enter_your_business_name_and")],
  ["02", tx("landing.sell_straight_away"), tx("landing.about_20_sample_products_are")],
  ["03", tx("landing.print_then_grow"), tx("landing.print_thermal_receipts_from_the")],
]);

function How() {
  return (
    <section id="how" className="scroll-mt-20 border-y border-slate-200 bg-white" data-testid="landing-how">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 sm:px-8 lg:grid-cols-[1fr_1.4fr]">
        <div>
          <div className="eyebrow !text-amber-600">{txt("landing.how_it_works")}</div>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{txt("landing.from_sign_up_to_first")}</h2>
          <DemoCTA id="how" tone="dark" className="mt-8" />
          <Link to="/how-it-works" data-testid="how-full-walkthrough-link" className="mt-6 inline-flex items-center gap-2 font-display font-bold text-slate-900 underline decoration-amber-500 decoration-2 underline-offset-4 hover:text-amber-700">
            {txt("landing.see_the_full_walkthrough_with")} <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="mt-6 grid grid-cols-3 gap-2">
            {["pos_desktop", "fefo_split", "receipt_80"].map((id) => (
              <Link key={id} to="/how-it-works" className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                <img src={`${BACKEND}/api/media/how-it-works/${id}.thumb.webp`} alt="" loading="lazy" className="h-20 w-full object-cover object-top" />
              </Link>
            ))}
          </div>
        </div>
        <ol className="relative space-y-4">
          {STEPS().map(([n, t, b]) => (
            <li key={n} className="tape flex gap-5 rounded-sm px-6 py-6" data-testid={`how-step-${n}`}>
              <span className="money text-3xl font-extrabold text-amber-500">{n}</span>
              <div>
                <h3 className="font-display text-lg font-bold text-slate-900">{t}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{b}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Pricing() {
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [currency, setCurrency] = useState(() => localStorage.getItem(PRICING_CUR_KEY) || "USD");
  const [detected, setDetected] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const save = Math.max(0, ...plans.filter((p) => !p.custom).map((p) => Number(p.yearly_discount) || 0));
  const picked = useRef(!!localStorage.getItem(PRICING_CUR_KEY));

  const pick = (c) => {
    picked.current = true;
    localStorage.setItem(PRICING_CUR_KEY, c);
    setCurrency(c);
  };

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    api.get("/geo/detect", { params: { tz, locale: navigator.language } }).then((r) => {
      setDetected(r.data);
      if (!picked.current) setCurrency(planCurrency(r.data.currency));
    }).catch(() => {});
  }, []);

  return (
    <section id="pricing" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-24 sm:px-8" data-testid="landing-pricing">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-xl">
          <div className="eyebrow !text-amber-600">{txt("landing.pricing")}</div>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{txt("landing.simple_monthly_plans_start_with")}</h2>
          <p className="mt-2 text-sm text-slate-500" data-testid="pricing-detected">
            {detected ? (detected.currency === currency && detected.country !== "OTHER" ? tx("landing.prices_shown_for", { currency, country: detected.name }) : tx("landing.prices_shown_in", { currency })) : tx("landing.detecting_your_currency")}
          </p>
        </div>
        <div className="flex gap-1 rounded-xl bg-slate-200/70 p-1" role="radiogroup" data-testid="currency-switcher">
          {CURRENCIES.map((c) => (
            <button key={c} role="radio" aria-checked={currency === c} onClick={() => pick(c)} data-testid={`currency-${c}`} className={`h-10 rounded-lg px-3 font-mono text-xs font-bold sm:px-4 ${currency === c ? "bg-slate-950 text-amber-400" : "text-slate-600 hover:text-slate-950"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-8 flex gap-1 self-start rounded-xl bg-slate-200/70 p-1" role="radiogroup" data-testid="billing-period-switcher" style={{ width: "fit-content" }}>
        {[["monthly", tx("landing.monthly")], ["yearly", tx("landing.yearly")]].map(([k, l]) => (
          <button key={k} role="radio" aria-checked={period === k} onClick={() => setPeriod(k)} data-testid={`billing-${k}`} className={`h-10 rounded-lg px-4 text-sm font-bold ${period === k ? "bg-slate-950 text-white" : "text-slate-600 hover:text-slate-950"}`}>
            {l}{k === "yearly" && save > 0 && <span className="ml-2 rounded-full bg-amber-500 px-2 py-0.5 font-mono text-[10px] text-slate-950" data-testid="billing-save-badge">Save {save}%</span>}
          </button>
        ))}
      </div>
      <div className="mt-8">
        <PlanCards plans={plans} currency={currency} period={period} mode="landing" onAction={() => nav("/signup")} />
        <AddonCard currency={currency} />
        <AddonCard code="reports" currency={currency} />
        <p className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600" data-testid="pricing-restaurant-note"><b className="text-slate-900">{txt("landing.restaurants")}</b> {txt("landing.every_plan_includes_tables_kots")} <b>{txt("landing.online_ordering_add_on")}</b> {txt("landing.menu_online_delivery_or_pickup")} <a href="#contact" className="font-semibold text-amber-700 underline">{txt("landing.ask_us")}</a> {txt("landing.to_switch_it_on")}</p>
      </div>
    </section>
  );
}

const FAQ = [
  [
  "Does it work for restaurants?",
  "Yes. Choose Restaurant when you sign up and you get a sample menu, sections and tables. Waiters open tables and send KOTs from a phone, the kitchen works from a tablet with New, In progress and Ready, and billing uses the same receipts, tax and reports as the shop POS."
  ],
  [
  "What is the Online Ordering add-on for restaurants?",
  "Your menu online by category, with delivery areas, pickup, opening hours and order tracking. Orders arrive as Received; Preparing sends them to the kitchen tagged Online, and Delivered or Collected records the sale. It's enabled per restaurant by our team."
  ],
  [
  "Does it work without internet?",
  "Yes. Once you have logged in on a device, the register keeps selling offline with cash or card, using receipt numbers reserved for that device. Sales sync automatically when the connection returns. Returns, credit notes, settings and imports need internet."
  ],
  [
  "How do returns and credit notes work?",
  "Scan the barcode on the receipt, pick the items and mark each one Resellable (back to stock) or Damaged. Refund in cash or issue a credit note. The customer uses the credit note in full on their next purchase at the same branch."
  ],
  [
  "What is the Inventory add-on?",
  "An optional add-on for Goods Receiving (GRN), put-away to shelf locations and location-aware picking: expiry first for pharmacies, oldest stock first for retail. Each cart line shows where to pick from."
  ],
  [
  "Can I sell online?",
  "Yes. The Online Store is a Premium feature included in Growth, Business and Enterprise, and in every free demo. Starter doesn't include it; upgrade any time and your store goes live."
  ],
  [
  "Do I get sales reports?",
  "Yes. Reports is a Premium feature included in Growth, Business and Enterprise and in every demo, or you can add the Reports add-on to any plan. See daily, weekly or monthly gross sales, discounts, returns, net revenue, tax and the cash/card/credit note split for all branches or one, plus Profit & Loss: revenue excluding tax, cost of goods sold, gross profit, margin and your top 10 products by profit. Export everything to CSV."
  ],
  [
  "How do customers find my store?",
  "Your store gets its own link, like retail.smrtin.net/your-shop. Share it on WhatsApp or social media, or print the QR code for your counter. Customers don't need an account."
  ],
  [
  "How do customers pay for online orders?",
  "Cash on delivery, or pay at pickup. There's nothing to set up. When you complete an order you choose Cash or Card and it becomes a normal sale with a printed receipt."
  ],
  [
  "Can I set delivery areas and fees?",
  "Yes. Add each area with its own delivery fee, set a minimum order for delivery, and turn delivery or pickup on and off. Weekly opening hours close the store automatically outside your hours."
  ],
  [
  "Can I print price tags and import products from Excel?",
  "Yes. Print shelf tags with barcodes in several layouts, and import or update hundreds of products from an Excel or CSV file, with a preview and row-by-row errors before anything is saved."
  ],
  [
  "Can I run several branches and staff?",
  "Yes. Each branch has its own receipt numbers and stock. Add managers and cashiers with their own logins, and choose what each role may do: discounts, reprints, products, returns and more."
  ],
  [
  "Which countries and taxes are supported?",
  "Every country. We preselect your country, currency, decimals and standard tax (VAT, GST, Sales Tax or Consumption Tax) from your location. In the USA and Canada you also pick your state or province. You can edit the rate and label in Settings at any time."
  ],
  [
  "How does the reseller program work?",
  "Apply with the Become a Reseller form. Once approved you get a referral link and code. Shops that sign up with it are linked to you, and you earn 20% of every subscription payment they make. Your portal shows performance, businesses and a downloadable statement."
  ],
  [
  "Do I need a credit card for the demo, and what happens after it?",
  "No card is needed. The 15-day demo is fully featured. When it ends the register locks but every product and sale is kept. Request a plan and our team reactivates you, usually the same day."
  ]
  ];

function Faq() {
  return (
    <section id="faq" className="scroll-mt-20 bg-white" data-testid="landing-faq">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-24 sm:px-8 lg:grid-cols-[1fr_1.6fr]">
        <div>
          <div className="eyebrow !text-amber-600">{txt("landing.nav_faq")}</div>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{txt("landing.questions_shop_owners_ask_us")}</h2>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {contentList("landing.faq", FAQ).map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`} className="border-slate-200">
              <AccordionTrigger className="text-left font-display text-base font-semibold text-slate-900 hover:no-underline" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-slate-600">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="bg-amber-500" data-testid="landing-final-cta">
      <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 py-16 sm:px-8 lg:flex-row lg:items-center">
        <h2 className="max-w-2xl font-display text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">{txt("landing.open_your_register_today_the")}</h2>
        <DemoCTA id="final" tone="dark" />
      </div>
    </section>
  );
}

export function Footer() {
  useContent();
  const contact = useContact();
  return (
    <footer className="grain bg-slate-950 text-slate-400" data-testid="landing-footer">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-8 md:grid-cols-4">
        <div className="md:col-span-2">
          <BrandMark />
          <p className="mt-4 max-w-sm text-sm">{txt("landing.cloud_point_of_sale_for")}</p>
        </div>
        <div className="space-y-2 text-sm">
          <div className="eyebrow !text-slate-500">{txt("landing.product")}</div>
          <a href="/#features" className="block hover:text-white">{txt("landing.features")}</a>
          <Link to="/how-it-works" className="block hover:text-white" data-testid="footer-how-link">{txt("landing.how_it_works")}</Link>
          <a href="/#pricing" className="block hover:text-white">{txt("landing.pricing")}</a>
          <Link to="/login" className="block hover:text-white">{txt("landing.log_in")}</Link>
          <button onClick={openResellerApply} className="block font-semibold text-amber-400 hover:text-amber-300" data-testid="footer-reseller-link">{txt("landing.become_a_reseller")}</button>
          <Link to="/reseller" className="block hover:text-white" data-testid="footer-reseller-login">{txt("landing.reseller_login")}</Link>
        </div>
        <div className="space-y-2 text-sm">
          <div className="eyebrow !text-slate-500">{txt("landing.contact")}</div>
          <button onClick={() => contact("support")} className="block font-semibold text-amber-400 hover:text-amber-300" data-testid="footer-contact-link">{txt("landing.contact_us")}</button>
          <a href={`mailto:${CONTACT_EMAIL}`} className="block hover:text-white" data-testid="footer-contact-email">{CONTACT_EMAIL}</a>
          <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-400" /> {txt("landing.your_data_stays_yours")}</span>
          <span className="flex items-center gap-1.5"><Boxes className="h-4 w-4 text-amber-400" /> {txt("landing.inventory_add_on_available")}</span>
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="mx-auto max-w-7xl px-4 py-5 font-mono text-[11px] uppercase tracking-widest text-slate-600 sm:px-8">© {new Date().getFullYear()} Smart-in Retail</div>
      </div>
    </footer>
  );
}

export default function Landing() {
  useContent();
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 150);
  }, [hash]);
  return (
    <div className="bg-slate-50" data-testid="landing-page">
      <Header />
      <Hero />
      <TaxStrip />
      <ProductsSection />
      <Features />
      <How />
      <OnlineStoreSection />
      <ReportsSection />
      <Pricing />
      <ResellerSection />
      <Faq />
      <FinalCTA />
      <Footer />
    </div>
  );
}
